import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTestContext } from '../context/TestContext';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../hooks/useTimer';
import { getTest } from '../api/tests';
import { submitResult } from '../api/results';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Timer } from '../components/test/Timer';
import { QuestionDisplay } from '../components/test/QuestionDisplay';
import { AnswerOptions } from '../components/test/AnswerOptions';
import { ScaleAnswer } from '../components/test/ScaleAnswer';
import { parseScaleConfig } from '../types/testData';
import { Input } from '../components/common/Input';
import { useLang } from '../context/LangContext';
import { fadeIn } from '../utils/animations';

/* ── Styled components ─────────────────────────────────────────── */

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  background: rgb(0, 149, 149);
`;

const TestCard = styled(Card)`
  max-width: 800px;
  width: 100%;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const PauseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.97);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  border-radius: inherit;
  z-index: 10;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const InfoOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 24px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.15s;
`;

const InfoPopup = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 480px;
  width: 100%;
`;

const InfoPopupTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoPopupText = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CustomAnswerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const CustomAnswerPopup = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 560px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CustomAnswerTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CustomAnswerTextarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 12px 14px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.cardBackground};

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const IconButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

const InfoIconButton = styled(IconButton)`
  font-weight: 700;
  font-style: italic;
  border-width: 2px;
`;

// Кнопка «Назад» — приглушённая, в тон кнопкам с вариантами ответов
// (серый контур/текст вместо яркого основного цвета).
const BackButton = styled(Button)`
  border: 2px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.textLight};
  min-height: 44px;

  &:hover:not(:disabled) {
    background-color: #dee0df;
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text};
    transform: none;
    box-shadow: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 46px;
  }
`;

// Палитра цветомера: 0 КрО, 1 ЛиЖ, 2 ТрЗ, 3 МаЛ, 4 ТёС, 5 ТёК, 6 АсС, 7 ЧёС
const COLOR_OPTIONS = ['#FA6400', '#FFFF00', '#00FF00', '#FF007C', '#0000CD', '#A0522D', '#808080', '#000000'];

// ИЦ[row=первый цвет][col=второй цвет]
const IC_TABLE: number[][] = [
  [0.98, 1.00, 0.90, 0.96, 0.76, 0.65, 0.44, 0.27],
  [0.99, 0.97, 0.84, 0.94, 0.75, 0.64, 0.43, 0.26],
  [0.87, 0.83, 0.78, 0.81, 0.74, 0.63, 0.42, 0.25],
  [0.92, 0.93, 0.86, 0.88, 0.73, 0.52, 0.41, 0.23],
  [0.71, 0.70, 0.69, 0.68, 0.60, 0.46, 0.33, 0.14],
  [0.56, 0.54, 0.50, 0.49, 0.46, 0.31, 0.29, 0.13],
  [0.40, 0.39, 0.38, 0.37, 0.17, 0.16, 0.15, 0.12],
  [0.21, 0.20, 0.19, 0.19, 0.11, 0.10, 0.09, 0.08],
];

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 28px;

  @media (min-width: 600px) {
    max-width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ColorCellWrapper = styled.div`
  position: relative;
`;

const ColorCell = styled.button<{ $color: string; $anySelected: boolean; $disabled: boolean }>`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 10px;
  border: 4px solid ${({ $anySelected }) => ($anySelected ? '#fff' : 'transparent')};
  outline: ${({ $anySelected }) => ($anySelected ? '3px solid #222' : '2px solid transparent')};
  outline-offset: 1px;
  background: ${({ $color }) => $color};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: transform 0.1s, border-color 0.1s;

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'scale(1.07)')};
  }
`;

const CellBadge = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 5px;
  ${({ $side }) => ($side === 'left' ? 'left: 7px;' : 'right: 7px;')}
  background: rgba(255, 255, 255, 0.92);
  color: #111;
  font-size: 11px;
  font-weight: 800;
  border-radius: 50%;
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  line-height: 1;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatusLabel = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const HiddenBadge = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textLight};
  font-style: italic;
`;

const LoadingMsg = styled.div`
  font-size: 1.5rem;
  color: white;
`;

const FormCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
`;

const FormTitle = styled.h2`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const FormSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};

  label {
    display: block;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-size: 0.95rem;
  }

  select {
    width: 100%;
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
    border: 2px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: 16px;
    background-color: ${({ theme }) => theme.colors.cardBackground};
    color: ${({ theme }) => theme.colors.text};
    transition: border-color ${({ theme }) => theme.transitions.fast};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const ResultsCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`;

const ResultRow = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

/* ── Component ──────────────────────────────────────────────────── */

const TestPage: React.FC = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const { user } = useAuth();
  const { currentTest, setCurrentTest } = useTestContext();
  const { t } = useLang();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // id выбранного ОтО (один клик) или список id кликов в порядке нажатия (мульти-режим).
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number | number[]>>({});
  const [questionTimes, setQuestionTimes] = useState<Record<number, number>>({});
  // Времена каждого клика в мс (мульти-режим): {question_id: [t1, t2, ...]}.
  const [clickTimes, setClickTimes] = useState<Record<number, number[]>>({});
  const [skipReasons, setSkipReasons] = useState<Record<number, 'skip' | 'timeout'>>({});
  const [showUserForm, setShowUserForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const startTimeRef = useRef<number>(0);
  const questionStartRef = useRef<number>(0);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigatingRef = useRef(false);
  const restoredStartTimeRef = useRef<number | null>(null);
  const restoredQuestionElapsedRef = useRef<number | null>(null);
  const [backUsed, setBackUsed] = useState(false);

  const [participantName, setParticipantName] = useState('');
  const [participantDevice, setParticipantDevice] = useState('');
  const [participantEmail, setParticipantEmail] = useState('my_e-mail@bk.com');
  const [participantAge, setParticipantAge] = useState('');
  const [participantGender, setParticipantGender] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [customAnswers, setCustomAnswers] = useState<Record<number, string>>({});
  const [customAnswerOpen, setCustomAnswerOpen] = useState(false);
  const [customAnswerDraft, setCustomAnswerDraft] = useState('');
  const [colorAnswers, setColorAnswers] = useState<Record<number, import('../types/testData').ColorAnswerData>>({});
  const [colorFirstPick, setColorFirstPick] = useState<Record<number, { idx: number; time: number }>>({});

  const advanceQuestion = () => {
    setBackUsed(false);
    setShowInfoPopup(false);
    if (currentQuestionIndex >= (currentTest?.questions.length ?? 1) - 1) {
      setShowUserForm(true);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  // Число кликов по ОтО до смены стимула: clicks_to_advance из редактора, по умолчанию 1.
  const clicksNeeded = (currentTest?.questions[currentQuestionIndex]?.clicks_to_advance ?? 1) || 1;

  // Per-question timer — onTimeUp always fresh via ref in useTimer
  const { time: qTime, start: startTimer, pause: pauseTimer, reset: resetTimer } = useTimer(
    0,
    () => {
      const question = currentTest?.questions[currentQuestionIndex];
      const isInstruction = !currentTest?.color_mode && (question?.answer_options?.length ?? 1) === 0;
      const hideLabels = currentTest?.color_mode && question?.parameters?.hide_color_labels === 'true';
      if (!isInstruction && !hideLabels && question?.id) {
        const elapsed = Math.round((Date.now() - questionStartRef.current) / 50) * 50;
        setQuestionTimes((prev) => ({ ...prev, [question.id]: elapsed }));
        setSkipReasons((prev) => ({ ...prev, [question.id]: 'timeout' }));
      }
      advanceQuestion();
    }
  );

  // Prevent opening the same test in multiple tabs of the same browser
  useEffect(() => {
    if (!testId) return;

    const ACTIVE_TEST_KEY = 'active_test_session';
    const SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours

    // Persistent tab ID lives in sessionStorage (cleared when tab closes)
    let tabId = sessionStorage.getItem('test_tab_id');
    if (!tabId) {
      tabId = Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem('test_tab_id', tabId);
    }

    const raw = localStorage.getItem(ACTIVE_TEST_KEY);
    if (raw) {
      try {
        const session = JSON.parse(raw);
        const expired = Date.now() - session.timestamp > SESSION_TIMEOUT_MS;
        if (!expired && session.testId === testId && session.tabId !== tabId) {
          navigate('/tests');
          return;
        }
      } catch {}
    }

    // Claim session for this tab
    localStorage.setItem(ACTIVE_TEST_KEY, JSON.stringify({ testId, tabId, timestamp: Date.now() }));

    const release = () => {
      const current = localStorage.getItem(ACTIVE_TEST_KEY);
      if (current) {
        try {
          const s = JSON.parse(current);
          if (s.tabId === tabId) localStorage.removeItem(ACTIVE_TEST_KEY);
        } catch {}
      }
    };

    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
      release();
      // Остановить отложенный автопереход, если тест покинули до срабатывания.
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [testId]); // eslint-disable-line

  // Load test
  useEffect(() => {
    if (!testId) { navigate('/tests'); return; }
    const load = async () => {
      setLoading(true);
      try {
        // Always fetch fresh — context may have stale time_limit after editing
        const data = await getTest(Number(testId));

        const rawDraft = sessionStorage.getItem(`test_draft_${testId}`);
        interface TestDraft {
          questionOrder?: number[];
          currentQuestionIndex?: number;
          selectedAnswers?: Record<number, number | number[]>;
          questionTimes?: Record<number, number>;
          clickTimes?: Record<number, number[]>;
          skipReasons?: Record<number, 'skip' | 'timeout'>;
          colorAnswers?: Record<number, import('../types/testData').ColorAnswerData>;
          colorFirstPick?: Record<number, { idx: number; time: number }>;
          customAnswers?: Record<number, string>;
          showUserForm?: boolean;
          backUsed?: boolean;
          timeSpentMs?: number;
          questionElapsedMs?: number;
        }
        let draft: TestDraft | null = null;
        if (rawDraft) {
          try {
            draft = JSON.parse(rawDraft) as TestDraft;
          } catch {
            // Повреждённый/устаревший черновик — начинаем тест заново.
            sessionStorage.removeItem(`test_draft_${testId}`);
          }
        }

        if (draft?.questionOrder) {
          // Restore saved question order (preserves shuffle)
          const orderMap: Record<number, number> = {};
          (draft.questionOrder as number[]).forEach((id: number, i: number) => { orderMap[id] = i; });
          data.questions = [...data.questions].sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));
        } else if (data.shuffle_ranges?.length) {
          const shuffled = [...data.questions];
          for (const [start, end] of data.shuffle_ranges) {
            const s = start - 1;
            const e = Math.min(end - 1, shuffled.length - 1);
            if (s < 0 || s >= e) continue;
            for (let i = e; i > s; i--) {
              const j = s + Math.floor(Math.random() * (i - s + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
          }
          data.questions = shuffled;
        }

        setCurrentTest(data);

        if (draft) {
          setCurrentQuestionIndex(draft.currentQuestionIndex ?? 0);
          setSelectedAnswers(draft.selectedAnswers ?? {});
          setQuestionTimes(draft.questionTimes ?? {});
          setClickTimes(draft.clickTimes ?? {});
          setSkipReasons(draft.skipReasons ?? {});
          setColorAnswers(draft.colorAnswers ?? {});
          setColorFirstPick(draft.colorFirstPick ?? {});
          setCustomAnswers(draft.customAnswers ?? {});
          setShowUserForm(draft.showUserForm ?? false);
          setBackUsed(draft.backUsed ?? false);
          restoredStartTimeRef.current = Date.now() - (draft.timeSpentMs ?? 0);
          restoredQuestionElapsedRef.current = draft.questionElapsedMs ?? 0;
        }
      } catch {
        navigate('/tests');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [testId]); // eslint-disable-line

  // Start elapsed-time tracking once test loaded
  useEffect(() => {
    if (!loading && currentTest) {
      if (restoredStartTimeRef.current !== null) {
        startTimeRef.current = restoredStartTimeRef.current;
        restoredStartTimeRef.current = null;
      } else {
        startTimeRef.current = Date.now();
      }
    }
  }, [loading, currentTest]);

  // Reset & start per-question timer when question index changes
  useEffect(() => {
    if (loading || !currentTest || showUserForm || showResults) return;
    const restoredElapsed = restoredQuestionElapsedRef.current;
    restoredQuestionElapsedRef.current = null;
    const elapsed = restoredElapsed ?? 0;
    questionStartRef.current = Date.now() - elapsed;
    const limit = currentTest.questions[currentQuestionIndex]?.time_limit ?? 0;
    const remaining = limit > 0 ? Math.max(1, limit - Math.floor(elapsed / 1000)) : 0;
    resetTimer(remaining > 0 ? remaining : limit);
    if (remaining > 0) startTimer();
  }, [currentQuestionIndex, currentTest?.id, loading]); // eslint-disable-line

  // Save draft to sessionStorage on every meaningful state change
  // (must be defined AFTER timer-reset effect so questionStartRef is up-to-date on question change)
  useEffect(() => {
    if (loading || !currentTest || showResults) return;
    const draft = {
      currentQuestionIndex,
      selectedAnswers,
      questionTimes,
      clickTimes,
      skipReasons,
      colorAnswers,
      colorFirstPick,
      customAnswers,
      showUserForm,
      backUsed,
      timeSpentMs: Date.now() - startTimeRef.current,
      questionElapsedMs: Date.now() - questionStartRef.current,
      questionOrder: currentTest.questions.map(q => q.id),
    };
    sessionStorage.setItem(`test_draft_${testId}`, JSON.stringify(draft));
  }, [currentQuestionIndex, selectedAnswers, questionTimes, clickTimes, skipReasons, colorAnswers, colorFirstPick, customAnswers, showUserForm, backUsed]); // eslint-disable-line

  // Режим «Шкала» (семантический дифференциал): ответ — число (балл шкалы),
  // фиксируется явной кнопкой «Далее».
  const handleScaleAnswer = (questionId: number, value: number) => {
    if (selectedAnswers[questionId] !== undefined) return;
    pauseTimer();
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 50) * 50;
    setQuestionTimes((prev) => ({ ...prev, [questionId]: elapsed }));
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: value }));
    autoAdvanceRef.current = setTimeout(() => {
      autoAdvanceRef.current = null;
      advanceQuestion();
    }, 400);
  };

  const handleAnswerSelect = (questionId: number, optionId: number) => {
    if (clicksNeeded > 1) {
      const made = (selectedAnswers[questionId] as number[]) ?? [];
      if (!Array.isArray(made) || made.length >= clicksNeeded) return;
      if (made.includes(optionId)) return; // дважды по одной клавише нельзя
      pauseTimer();
      const elapsed = Math.round((Date.now() - questionStartRef.current) / 50) * 50;
      const next = [...made, optionId];
      setSelectedAnswers((prev) => ({ ...prev, [questionId]: next }));
      setClickTimes((prev) => ({ ...prev, [questionId]: [...(prev[questionId] ?? []), elapsed] }));
      // Смена стимула только после набранного числа кликов
      if (next.length >= clicksNeeded) {
        setQuestionTimes((prev) => ({ ...prev, [questionId]: elapsed }));
        autoAdvanceRef.current = setTimeout(() => {
          autoAdvanceRef.current = null;
          advanceQuestion();
        }, 400);
      }
      return;
    }
    // Одиночный режим (по умолчанию): прежнее поведение.
    if (selectedAnswers[questionId] !== undefined) return;
    pauseTimer();
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 50) * 50;
    setQuestionTimes((prev) => ({ ...prev, [questionId]: elapsed }));
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: optionId }));

    autoAdvanceRef.current = setTimeout(() => {
      autoAdvanceRef.current = null;
      advanceQuestion();
    }, 400);
  };

  // [CLINE] Логика выбора пары цветов: 1-й клик → firstPick, 2-й клик → answer с ИЦ
  const handleColorSelect = (questionId: number, colorIndex: number) => {
    if (colorAnswers[questionId] !== undefined) return;
    const elapsed = Math.round((Date.now() - questionStartRef.current) / 50) * 50;
    const existing = colorFirstPick[questionId];
    if (!existing) {
      // Первое нажатие — запоминаем цвет и время
      setColorFirstPick((prev) => ({ ...prev, [questionId]: { idx: colorIndex, time: elapsed } }));
    } else {
      // Второе нажатие — фиксируем пару
      pauseTimer();
      const timeFirst = existing.time;
      const timeSecond = elapsed - timeFirst;
      const timeTotal = elapsed;
      const ic = IC_TABLE[existing.idx][colorIndex];
      const answer = { first: existing.idx, second: colorIndex, time_first: timeFirst, time_second: timeSecond, time_total: timeTotal, ic };
      setColorAnswers((prev) => ({ ...prev, [questionId]: answer }));
      setQuestionTimes((prev) => ({ ...prev, [questionId]: timeTotal }));
      autoAdvanceRef.current = setTimeout(() => {
        autoAdvanceRef.current = null;
        advanceQuestion();
      }, 400);
    }
  };

  const handlePause = () => {
    if (isPaused) {
      setIsPaused(false);
      const limit = currentTest?.questions[currentQuestionIndex]?.time_limit ?? 0;
      if (limit > 0) startTimer();
    } else {
      setIsPaused(true);
      pauseTimer();
    }
  };

  const handleFinish = () => {
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    pauseTimer();
    setShowUserForm(true);
  };

  const openCustomAnswerModal = () => {
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    setCustomAnswerDraft(customAnswers[currentQuestion.id] ?? '');
    setCustomAnswerOpen(true);
  };

  const closeCustomAnswerModal = () => {
    setCustomAnswerOpen(false);
  };

  const submitCustomAnswerModal = () => {
    const text = customAnswerDraft.trim();
    if (!text) return;
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    pauseTimer();
    setCustomAnswers((prev) => ({ ...prev, [currentQuestion.id]: text }));
    setCustomAnswerOpen(false);
    advanceQuestion();
  };

  const clearDraft = () => sessionStorage.removeItem(`test_draft_${testId}`);

  const handleUserFormSubmit = async () => {
    if (!currentTest) return;
    // Имя, устройство и e-mail необязательны; при пустом e-mail подставляем значение по умолчанию.
    const email = participantEmail.trim() || 'my_e-mail@bk.com';
    setSubmitting(true);
    setSaveError('');

    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);

    try {
      const saved = await submitResult(currentTest.id, {
        score: 0,
        time_spent: elapsed,
        answers: selectedAnswers,
        question_times: questionTimes,
        click_times: Object.keys(clickTimes).length > 0 ? clickTimes : undefined,
        skip_reasons: Object.keys(skipReasons).length > 0 ? skipReasons : undefined,
        custom_answers: Object.keys(customAnswers).length > 0 ? customAnswers : undefined,
        color_answers: Object.keys(colorAnswers).length > 0 ? colorAnswers : undefined,
        participant_info: {
          name: participantName || undefined,
          device: participantDevice,
          email: email || undefined,
          age: participantAge ? Number(participantAge) : undefined,
          gender: participantGender || undefined,
        },
      });
      if (saved?.share_code) setShareCode(saved.share_code);
    } catch (err) {
      console.error('Ошибка сохранения результата:', err);
      setSaveError(t('formSubmitError'));
      return; // черновик остаётся, экран успеха не открывается
    } finally {
      setSubmitting(false);
    }

    clearDraft();
    setShowUserForm(false);
    setShowResults(true);
  };

  const filterName = (str: string) => str.slice(0, 50);

  const filterEmail = (str: string) =>
    str.replace(/[^a-zA-Z0-9@._+-]/g, '').slice(0, 100);

  /* ── Loading ── */
  if (loading) {
    return (
      <PageContainer>
        <LoadingMsg>{t('testLoading')}</LoadingMsg>
      </PageContainer>
    );
  }

  if (!currentTest) return <div>{t('testNotFound')}</div>;

  if (currentTest.questions.length === 0) {
    return (
      <PageContainer>
        <LoadingMsg style={{ fontSize: '1.2rem' }}>{t('testNoStimuli')}</LoadingMsg>
      </PageContainer>
    );
  }

  /* ── User info form ── */
  if (showUserForm) {
    return (
      <PageContainer>
        <FormCard>
          <FormTitle>{t('formTitle')}</FormTitle>
          <FormSubtitle>{t('formSubtitle')}</FormSubtitle>

          <FormGroup>
            <label>{t('formName')}</label>
            <Input
              value={participantName}
              onChange={(e) => setParticipantName(filterName(e.target.value))}
              placeholder={t('formNamePlaceholder')}
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <label>{t('formDevice')}</label>
            <select
              value={participantDevice}
              onChange={(e) => setParticipantDevice(e.target.value)}
            >
              <option value="">{t('formDeviceDefault')}</option>
              <option value="Компьютер/ноутбук">{t('formDevicePC')}</option>
              <option value="Телефон">{t('formDevicePhone')}</option>
              <option value="Планшет">{t('formDeviceTablet')}</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>{t('formEmail')}</label>
            <Input
              type="email"
              value={participantEmail}
              onChange={(e) => setParticipantEmail(filterEmail(e.target.value))}
              placeholder={t('formEmailPlaceholder')}
              fullWidth
            />
          </FormGroup>

          <div style={{ display: 'flex', gap: '12px' }}>
            <FormGroup style={{ flex: 1 }}>
              <label>{t('formAge')} <span style={{ fontWeight: 400, opacity: 0.6 }}>{t('formNameOptional')}</span></label>
              <Input
                type="number"
                value={participantAge}
                onChange={(e) => setParticipantAge(e.target.value)}
                placeholder={t('formAgePlaceholder')}
                fullWidth
              />
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <label>{t('formGender')} <span style={{ fontWeight: 400, opacity: 0.6 }}>{t('formNameOptional')}</span></label>
              <select
                value={participantGender}
                onChange={(e) => setParticipantGender(e.target.value)}
              >
                <option value="">{t('formGenderNone')}</option>
                <option value="Мужской">{t('formGenderMale')}</option>
                <option value="Женский">{t('formGenderFemale')}</option>
              </select>
            </FormGroup>
          </div>

          {saveError && (
            <div style={{ color: '#c0392b', marginBottom: '12px', fontSize: '0.95rem' }} role="alert">
              {saveError}
            </div>
          )}

          <Button
            variant="primary"
            size="lg"
            onClick={handleUserFormSubmit}
            disabled={submitting}
            fullWidth
            style={{ marginTop: '8px' }}
          >
            {submitting ? t('formSubmitting') : t('formSubmit')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => { clearDraft(); navigate('/tests'); }}
            disabled={submitting}
            fullWidth
            style={{ marginTop: '8px' }}
          >
            {t('formSkipSave')}
          </Button>
        </FormCard>
      </PageContainer>
    );
  }

  /* ── Thank you screen ── */
  if (showResults) {
    return (
      <PageContainer>
        <ResultsCard>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '16px' }}>
            {t('thanksTitle')}
          </h1>
          {shareCode && (
            <div style={{ margin: '24px 0', padding: '16px', background: 'rgba(0,0,0,0.04)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>{t('shareCodeTitle')}</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '0.2em', marginBottom: '12px' }}>{shareCode}</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { navigator.clipboard.writeText(shareCode); setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }}
              >
                {codeCopied ? t('shareCodeCopied') : t('shareCodeCopy')}
              </Button>
            </div>
          )}

          <ResultRow style={{ marginTop: '16px' }}>
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                if (user?.role === 'admin') {
                  navigate('/results');
                } else if (shareCode) {
                  navigate(`/result/${encodeURIComponent(shareCode)}`);
                } else {
                  navigate('/results-gates');
                }
              }}
              style={{ marginTop: '8px' }}
            >
              {t('thanksResultsLink')}
            </Button>
          </ResultRow>
          <ResultRow style={{ marginTop: '16px' }}>{t('thanksContact')}</ResultRow>
          <ResultRow>
            <a href="mailto:psydecrypt@mail.ru" style={{ color: 'inherit' }}>
              psydecrypt@mail.ru
            </a>
          </ResultRow>
          <ResultRow>
            <a href="https://www.psydecrypt.online" style={{ color: 'inherit' }}>
              www.psydecrypt.online
            </a>
          </ResultRow>
        </ResultsCard>
      </PageContainer>
    );
  }

  /* ── Test in progress ── */
  const currentQuestion = currentTest.questions[currentQuestionIndex];
  const hasTimeLimit = (currentQuestion?.time_limit ?? 0) > 0;
  const isColorMode = !!currentTest.color_mode;
  const isScale = !isColorMode && parseScaleConfig(currentQuestion.parameters).answer_form === 'scale';
  // Инструкция — стимул без вариантов и без шкалы (нет средств ответа).
  const isInstruction = !isColorMode && !isScale && currentQuestion.answer_options.length === 0;
  const hideLabels = isColorMode && currentQuestion?.parameters?.hide_color_labels === 'true';

  let prevIdx = currentQuestionIndex - 1;
  while (prevIdx >= 0 && !isColorMode && currentTest.questions[prevIdx].answer_options.length === 0) prevIdx--;
  const showBack = prevIdx >= 0 && !backUsed;

  // Панель внизу карточки (Назад / поле своего ответа / кнопка ввода).
  // Для инструкций и стимулов со скрытыми метками панели нет; иначе область резервируется
  // постоянно, чтобы высота карточки не «прыгала» при смене стимула, а её содержимое
  // (кнопка «Назад», textarea, кнопка ввода) появляется по мере возникновения.
  const showAnswerInputArea = isColorMode ? !hideLabels : !isInstruction;
  const hasLeftPanel = showBack || showAnswerInputArea;

  return (
    <PageContainer>
      <TestCard>
        <StatusBar>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <StatusLabel>
              {currentQuestionIndex + 1} {t('questionCounter')} {currentTest.questions.length}
            </StatusLabel>
            {clicksNeeded > 1 && (
              <StatusLabel>
                {t('clicksProgress')
                  .replace('{k}', String(((selectedAnswers[currentQuestion.id] as number[]) ?? []).length))
                  .replace('{n}', String(clicksNeeded))}
              </StatusLabel>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {currentQuestion.is_hidden && user?.role === 'admin' && (
              <HiddenBadge>{t('editorHiddenBadge')}</HiddenBadge>
            )}
            {hasTimeLimit && <Timer seconds={qTime} />}
            {currentQuestion.info && (
              <InfoIconButton onClick={() => setShowInfoPopup(true)} title={t('testInfoTooltip')}>
                ℹ
              </InfoIconButton>
            )}
            <IconButton onClick={handlePause} title={isPaused ? t('testResume') : undefined}>
              {isPaused ? '▶' : '⏸'}
            </IconButton>
            <IconButton onClick={handleFinish}>
              ✕
            </IconButton>
          </div>
        </StatusBar>

        {isPaused && (
          <PauseOverlay>
            <span>{t('testPaused')}</span>
            <Button variant="primary" size="lg" onClick={handlePause}>
              {t('testResume')}
            </Button>
          </PauseOverlay>
        )}

        <InfoOverlay $visible={showInfoPopup} onClick={() => setShowInfoPopup(false)}>
          <InfoPopup onClick={(e) => e.stopPropagation()}>
            <InfoPopupTitle>{t('testInfoTitle')}</InfoPopupTitle>
            <InfoPopupText>{currentQuestion.info}</InfoPopupText>
            <Button variant="primary" size="md" onClick={() => setShowInfoPopup(false)} fullWidth>
              {t('testInfoClose')}
            </Button>
          </InfoPopup>
        </InfoOverlay>

        <QuestionDisplay
          question={currentQuestion}
          colorMode={isColorMode}
        />

        {isColorMode && !hideLabels && (
          <ColorGrid>
            {COLOR_OPTIONS.map((color, idx) => {
              const completed = colorAnswers[currentQuestion.id];
              const pending = colorFirstPick[currentQuestion.id];
              const firstIdx = completed ? completed.first : (pending?.idx ?? -1);
              const secondIdx = completed ? completed.second : -1;
              const isFirst = firstIdx === idx;
              const isSecond = secondIdx === idx && completed !== undefined;
              const isDisabled = completed !== undefined;
              return (
                <ColorCellWrapper key={idx}>
                  <ColorCell
                    $color={color}
                    $anySelected={isFirst || isSecond}
                    $disabled={isDisabled}
                    onClick={() => handleColorSelect(currentQuestion.id, idx)}
                  />
                  
                  {isFirst && <CellBadge $side="left">1</CellBadge>}
                  {isSecond && <CellBadge $side="right">2</CellBadge>}
                </ColorCellWrapper>
              );
            })}
          </ColorGrid>
        )}

        {/* Скрытые метки: только кнопка «Продолжить», без выбора цвета */}
        {hideLabels && (
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
            <Button variant="primary" size="lg" onClick={advanceQuestion}>
              {t('btnOk')}
            </Button>
          </div>
        )}

        {!isColorMode && !isInstruction && parseScaleConfig(currentQuestion.parameters).answer_form === 'scale' && (
          <ScaleAnswer
            config={parseScaleConfig(currentQuestion.parameters)}
            value={selectedAnswers[currentQuestion.id] as number | undefined}
            disabled={selectedAnswers[currentQuestion.id] !== undefined}
            onConfirm={(value) => handleScaleAnswer(currentQuestion.id, value)}
          />
        )}

        {!isColorMode && !isInstruction && parseScaleConfig(currentQuestion.parameters).answer_form !== 'scale' && (
          <>
            <AnswerOptions
              options={currentQuestion.answer_options}
              selectedOptionId={
                clicksNeeded > 1
                  ? ((selectedAnswers[currentQuestion.id] as number[]) ?? [])
                  : (selectedAnswers[currentQuestion.id] as number)
              }
              onOptionSelect={(optionId) => handleAnswerSelect(currentQuestion.id, optionId)}
              disabled={
                clicksNeeded > 1
                  ? (((selectedAnswers[currentQuestion.id] as number[]) ?? []).length >= clicksNeeded)
                  : (selectedAnswers[currentQuestion.id] !== undefined)
              }
            />
          </>
        )}

        {isInstruction && !hasTimeLimit && (
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="primary"
              size="lg"
              onClick={advanceQuestion}
            >
              {t('btnOk')}
            </Button>
          </div>
        )}

        {hasLeftPanel && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0', minHeight: 92, display: 'flex', justifyContent: currentQuestion.allow_custom_answer ? 'space-between' : 'center', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: '0 0 auto' }}>
              {showBack && (
                <BackButton
                  variant="outline"
                  size="sm"
                  style={{ whiteSpace: 'nowrap', width: '130px' }}
                  onClick={() => {
                    if (navigatingRef.current) return;
                    navigatingRef.current = true;
                    setTimeout(() => { navigatingRef.current = false; }, 300);
                    setBackUsed(true);
                    if (autoAdvanceRef.current) {
                      clearTimeout(autoAdvanceRef.current);
                      autoAdvanceRef.current = null;
                    }
                    pauseTimer();
                    const idsToReset = currentTest.questions
                      .slice(prevIdx, currentQuestionIndex + 1)
                      .map((q) => q.id);
                    setSelectedAnswers((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setQuestionTimes((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setSkipReasons((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setColorAnswers((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setColorFirstPick((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setClickTimes((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      return next;
                    });
                    setCurrentQuestionIndex(prevIdx);
                  }}
                >
                  {t('btnBack')}
                </BackButton>
              )}
            </div>
            {currentQuestion.allow_custom_answer && (
              <Button
                variant="outline"
                size="sm"
                style={{ whiteSpace: 'nowrap' }}
                onClick={openCustomAnswerModal}
              >
                {t('customAnswerOpen')}
              </Button>
            )}
          </div>
        )}
      </TestCard>

      {currentQuestion.allow_custom_answer && customAnswerOpen && (
        <CustomAnswerOverlay onClick={closeCustomAnswerModal}>
          <CustomAnswerPopup onClick={(e) => e.stopPropagation()}>
            <CustomAnswerTitle>{t('customAnswerTitle')}</CustomAnswerTitle>
            <CustomAnswerTextarea
              autoFocus
              value={customAnswerDraft}
              onChange={(e) => setCustomAnswerDraft(e.target.value)}
              placeholder={t('customAnswerPlaceholder')}
              maxLength={1000}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <Button variant="outline" size="md" onClick={closeCustomAnswerModal}>
                {t('cancelButton')}
              </Button>
              <Button
                variant="primary"
                size="md"
                disabled={!customAnswerDraft.trim()}
                onClick={submitCustomAnswerModal}
              >
                {t('btnSubmitCustomAnswer')}
              </Button>
            </div>
          </CustomAnswerPopup>
        </CustomAnswerOverlay>
      )}
    </PageContainer>
  );
};

export default TestPage;
